import React, { ReactNode, useMemo } from "react";
import { Button } from "primereact/button";
import { InventoryObjectTableEntry } from "../../apis/armoryApi";
import BaseDataTable, { ColumnMeta } from "./BaseDataTable";
import { useNavigate } from "react-router-dom";

interface InventoryObjectDataTableProps {
  isLoading: boolean;
  inventoryObjectsData: InventoryObjectTableEntry[];
  onRefreshButtonClick: () => void;
  columnOverrides?: ColumnMeta<InventoryObjectTableEntry>[];
  additionalActions?: ReactNode;
}

const InventoryObjectDataTable: React.FC<InventoryObjectDataTableProps> = ({
  isLoading,
  inventoryObjectsData,
  onRefreshButtonClick,
  columnOverrides = [],
  additionalActions,
}) => {
  const navigate = useNavigate();

  const defaultColumns: ColumnMeta<InventoryObjectTableEntry>[] = [
    {
      header: "Manage",
      field: "id",
      body: (rowData: InventoryObjectTableEntry) => (
        <Button 
          icon="pi pi-cog" 
          className="p-button-rounded p-button-info" 
          title={`Manage Asset ${rowData.serial}`} 
          tooltip={`Manage Asset ${rowData.serial}`} 
          tooltipOptions={{ position: "top" }} 
          onClick={() => navigate(`/asset/${rowData.serial}`)} 
        />
      ),
      filter: false,
      isSortable: false,
      isVisible: true,
      isCopyable: false
    },
    {
      header: "ID",
      field: "id",
      filter: true,
      filterPlaceholder: "Search by Asset ID",
      isSortable: true,
      isVisible: false,
      isCopyable: true,
    },
    {
      header: "Type",
      field: "skuName",
      filter: true,
      filterPlaceholder: "Search by Type",
      isSortable: true,
      isVisible: true,
      isCopyable: true
    },
    {
      header: "Serial",
      field: "serial",
      filter: true,
      filterPlaceholder: "Search by Asset Serial",
      isSortable: true,
      isVisible: true,
      isCopyable: true
    },
    {
      header: "Lessee Name",
      field: "lesseeName",
      filter: true,
      filterPlaceholder: "Search by Lessee Name",
      isSortable: true,
      isVisible: true,
      isCopyable: true
    },
    {
      header: "Lessee Id",
      field: "lesseeId",
      filter: true,
      filterPlaceholder: "Search by Lessee Id",
      isSortable: true,
      isVisible: true,
      isCopyable: true
    },
    {
      header: "Status",
      field: "status",
      filter: true,
      filterPlaceholder: "Search by Status",
      isSortable: true,
      isVisible: true,
      isCopyable: true
    },
  ];
  
    const columns = useMemo(() => {
      return defaultColumns.map((col) => {
        const override = columnOverrides.find((o) => o.field === col.field);
        return override ? { ...col, ...override } : col;
      });
    }, [columnOverrides]);

  return (
    <BaseDataTable
      isLoading={isLoading}
      data={inventoryObjectsData}
      columns={columns}
      onRefreshButtonClick={onRefreshButtonClick}
      additionalActions={additionalActions}
    />
  );
};

export default InventoryObjectDataTable;